import { useEffect, useState } from 'react';
import { useAtomValue as useStateSegmentValue } from 'jotai';
import get from 'lodash/get';
import { RiFileForbidLine } from '@remixicon/react';
import BrandsInfoTable from '../../BrandsInfoTable';
import styles from './SegmentsTab.styles.module.css';
import {
  knowledgeCenterStateSegment,
  SegmentData,
} from '../../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import { translate } from '../../../../localization';
import Loading from '../../Loading';
import getFormattedCustomerType from '../../../../utils/getFormattedCustomerType';
import useBrandMessageState from '../../../../hooks/brandInfoPage/useBrandMessageState';
import BrandsMessage from '../BrandsMessage';
import InfoLogo from '../InfoLogo';
import Button from '../../../atoms/Button';
import Retry from '../Retry';
import { fetchSegmentDetails } from '../../../../store/markettingContentGeneration/actions/majorProcessActions/fetchSegmentDetails';

interface SegmentsTabProps {
  isNonOnboardedBrand?: boolean;
}

export default function SegmentsTab({
  isNonOnboardedBrand = false,
}: SegmentsTabProps) {
  const { segmentDetails, apiPayloadUsed } = useStateSegmentValue(
    knowledgeCenterStateSegment
  );

  const { data, isLoading, errorMessage } = segmentDetails || {};

  const segments = get(data, 'segments', [] as SegmentData[]);

  const [retryCount, setRetryCount] = useState(0);

  const columnsConfiguration = [
    {
      title: translate('segment_name_column_header'),
      sortable: true,
      showInfoIcon: false,
      value: 'name',
    },
    {
      title: translate('marketingbox_indication_label'),
      sortable: false,
      showInfoIcon: false,
      value: 'indication',
    },
    {
      title: translate('segment_description_column_header'),
      sortable: false,
      showInfoIcon: false,
      value: 'description',
    },
  ];

  const {
    state: brandMessageConfig,
    updateScenarioNoData,
    updateScenarioTabLevelError,
    updateScenarioTabLevelApiDown,
  } = useBrandMessageState();

  const handleTabLevelRetry = async () => {
    const success = await fetchSegmentDetails(apiPayloadUsed);

    if (success) {
      setRetryCount(0);
    } else {
      setRetryCount(prev => prev + 1);
    }
  };

  /**
   * Checks if at least one entry in the data array has a non-empty `segments` array.
   *
   * @param {SegmentData[]} segmentData - The array of segment data objects.
   * @returns {boolean} Returns `true` if at least one object has a non-empty `segments` array, otherwise `false`.
   */
  const hasValidSegments = (segmentData: SegmentData[]): boolean =>
    segmentData?.some(
      entry => Array.isArray(entry?.segments) && entry?.segments?.length > 0
    );

  useEffect(() => {
    // if there is no data inside the claims, then this `useEffect` will handle it
    if (!isLoading && errorMessage?.length === 0 && segments?.length === 0) {
      updateScenarioNoData({
        logo: <RiFileForbidLine size={64} color='#A1A1AA' />,
        title: translate('segments_no_data_title'),
        message: translate('segments_no_data_message'),
      });
    }
  }, [isLoading, segments?.length]);

  useEffect(() => {
    if (errorMessage.length > 0) {
      updateScenarioTabLevelError(
        <InfoLogo />,
        <Button
          type='button'
          size='m'
          variant='outline'
          colorScheme='#0000C9'
          onClick={handleTabLevelRetry}
        >
          <Retry />
        </Button>
      );
    }
  }, [errorMessage.length]);

  useEffect(() => {
    if (retryCount > 3) {
      updateScenarioTabLevelApiDown(<InfoLogo />);
    }
  }, [retryCount]);

  useEffect(() => {
    // if the `data.segments` array has length > 0, but all of the `segments` attributes
    // inside every array of the object have length === 0, then this `useEffect` will handle it
    if (!isLoading && segments?.length > 0 && !hasValidSegments(segments)) {
      updateScenarioNoData({
        logo: <RiFileForbidLine size={64} color='#A1A1AA' />,
        title: translate('segments_no_data_title'),
        message: translate('segments_no_data_message'),
      });
    }
  }, [segments]);

  // Early return for non-onboarded brands
  if (isNonOnboardedBrand) {
    return (
      <div className={styles['brands-info__loading']}>
        <BrandsMessage
          logo={<RiFileForbidLine size={64} color='#A1A1AA' />}
          title={translate('segments_no_data_title')}
          message={translate('segments_no_data_message')}
          cta={null}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles['brands-info__loading']}>
        <Loading />
      </div>
    );
  }

  const renderNoDataMessage = () => {
    return (
      <div className={styles['brands-info__loading']}>
        <BrandsMessage
          logo={brandMessageConfig.logo}
          title={brandMessageConfig.title}
          message={brandMessageConfig.message}
          cta={brandMessageConfig.cta}
        />
      </div>
    );
  };

  if (!isLoading && (segments.length === 0 || errorMessage.length > 0)) {
    return renderNoDataMessage();
  }

  return (
    <div>
      {data && segments && hasValidSegments(segments) ? (
        <>
          {segments?.map(({ audience, segments: segmentsData }) => {
            // Add indication field to each segment for table display
            // Since indications is now an array within each segment, we'll use the first indication or join them
            const segmentsWithIndication = segmentsData.map(segment => ({
              ...segment,
              indication: Array.isArray(segment.indications)
                ? segment.indications.join(', ')
                : segment.indications || '',
            }));

            return (
              <div key={`${audience}`}>
                <div className={styles['brands-info__segments_tab_head']}>
                  <span
                    className={styles['brands-info__segments_tab_head_title']}
                  >
                    {translate('applicable_customer_type')}
                  </span>
                  <span
                    className={
                      styles['brands-info__segments_tab_head_subtitle']
                    }
                  >
                    {getFormattedCustomerType(audience)}
                  </span>
                </div>

                <BrandsInfoTable
                  columnsConfiguration={columnsConfiguration}
                  tableData={segmentsWithIndication}
                  gridColumnsStyle='33% 34% 33%'
                />
              </div>
            );
          })}
        </>
      ) : (
        renderNoDataMessage()
      )}
    </div>
  );
}
